import { logService, LOG_CATEGORIES } from '@/services/logService'
import type { Tour } from '@/types/tour'

const STORAGE_KEY_PREFIX = 'routecamp-workspace-tour-visibility:'

type PersistedTourVisibility = {
  visible: boolean
  tracks: Record<string, boolean>
}

type PersistedWorkspaceTourVisibility = {
  version: 1
  tours: Record<string, PersistedTourVisibility>
}

function getStorageKey(workspaceId: string): string {
  return `${STORAGE_KEY_PREFIX}${workspaceId}`
}

function getLocalStorage(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage
}

function readPersistedWorkspaceTourVisibility(
  workspaceId: string,
): PersistedWorkspaceTourVisibility | null {
  const storage = getLocalStorage()
  if (!storage || !workspaceId) return null

  try {
    const raw = storage.getItem(getStorageKey(workspaceId))
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<PersistedWorkspaceTourVisibility>
    if (parsed.version !== 1 || !parsed.tours || typeof parsed.tours !== 'object') return null

    return parsed as PersistedWorkspaceTourVisibility
  } catch (error) {
    logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to read local tour visibility state', {
      workspaceId,
      error,
    })
    return null
  }
}

export function persistWorkspaceTourVisibility(workspaceId: string, tours: Tour[]): void {
  const storage = getLocalStorage()
  if (!storage || !workspaceId) return

  const snapshot: PersistedWorkspaceTourVisibility = {
    version: 1,
    tours: {},
  }

  for (const tour of tours) {
    if (!tour.clientId) continue

    snapshot.tours[tour.clientId] = {
      visible: tour.visible !== false,
      tracks: Object.fromEntries(
        tour.tracks
          .filter((track) => Boolean(track.clientId))
          .map((track) => [track.clientId, track.visible !== false]),
      ),
    }
  }

  try {
    storage.setItem(getStorageKey(workspaceId), JSON.stringify(snapshot))
  } catch (error) {
    logService.warn(LOG_CATEGORIES.GENERAL, 'Failed to persist local tour visibility state', {
      workspaceId,
      error,
    })
  }
}

export function applyPersistedWorkspaceTourVisibility(workspaceId: string, tours: Tour[]): void {
  const persisted = readPersistedWorkspaceTourVisibility(workspaceId)
  if (!persisted) return

  for (const tour of tours) {
    const persistedTour = persisted.tours[tour.clientId]
    if (!persistedTour) continue

    tour.visible = persistedTour.visible
    for (const track of tour.tracks) {
      if (!track.clientId) continue
      track.visible = persistedTour.tracks[track.clientId] ?? persistedTour.visible
    }
  }
}
